// Helpers for reading the order details payload returned by
// /orderHead-management/managed-orderHead/{orderHeadId}. The restaurant order
// panel and the settle bill dialog both render the same server-side order, so
// the shape is normalized in one place.

export const TAX_RATE = 0.05;

export const getOrderItems = (cartData) => cartData?.orderDetails?.items || [];

export const getItemTitle = (line) =>
  line?.title || line?.prodName || line?.itemName || "Item";

export const getLineAmount = (line) =>
  (Number(line?.amt) || 0) * (Number(line?.qty) || 0);

export const summarizeOrderDetails = (cartData) => {
  const items = getOrderItems(cartData);

  const subtotal = items.reduce((sum, line) => sum + getLineAmount(line), 0);

  const tax = Number(cartData?.orderDetails?.totTax) || 0;
  const charges = Number(cartData?.orderDetails?.totChg) || 0;
  const offer = Number(cartData?.orderDetails?.totOff) || 0;
  const coupon = Number(cartData?.orderDetails?.couponAmount) || 0;

  return {
    subtotal,
    tax,
    charges,
    offer,
    coupon,
    total: Math.max(0, subtotal + tax + charges - offer - coupon),
    qty: items.reduce((sum, line) => sum + (Number(line?.qty) || 0), 0),
  };
};

// --- Offers -----------------------------------------------------------------
// orderDetails.offerDetails is a list of per-item discounts:
//   { offerName: "Item #1 Discount", offerPercentage, originalAmount,
//     discountedAmount, savedAmount }
// The API does not reference the line it belongs to, so we match each offer to
// a line whose amount equals discountedAmount (line total first, then unit
// price), and fall back to the index in the offer name ("Item #N").

const toNum = (value) => Number(value) || 0;
const sameAmount = (a, b) => Math.abs(toNum(a) - toNum(b)) < 0.005;

export const getOfferDetails = (orderDet) =>
  Array.isArray(orderDet?.offerDetails) ? orderDet.offerDetails : [];

export const sumOfferSavings = (orderDet) =>
  getOfferDetails(orderDet).reduce(
    (sum, offer) => sum + toNum(offer?.savedAmount),
    0
  );

export const matchOffersToItems = (items = [], orderDet) => {
  const offers = getOfferDetails(orderDet);
  const result = new Map();
  if (!offers.length || !items?.length) return result;

  const lineKey = (line, index) =>
    line?.orderDetId ?? line?.prodDefId ?? `idx-${index}`;
  const taken = new Set();

  const claim = (offer, predicate) => {
    const index = items.findIndex(
      (line, i) => !taken.has(i) && predicate(line, i)
    );
    if (index === -1) return false;
    taken.add(index);
    result.set(lineKey(items[index], index), offer);
    return true;
  };

  offers.forEach((offer) => {
    if (claim(offer, (line) => sameAmount(getLineAmount(line), offer?.discountedAmount)))
      return;
    if (claim(offer, (line) => sameAmount(line?.amt, offer?.discountedAmount)))
      return;
    const n = parseInt(String(offer?.offerName || "").match(/#\s*(\d+)/)?.[1], 10);
    if (!Number.isNaN(n)) claim(offer, (_, i) => i === n - 1);
  });

  return result;
};

export const getItemOffer = (offerMap, line, index) =>
  offerMap?.get(line?.orderDetId ?? line?.prodDefId ?? `idx-${index}`) || null;
